import React from 'react';

import { useGlobalContext } from '../../../context/global';
import { focusFieldById } from '../../utils/helper';
import { OverViewHeader } from '../../utils/hooks';
import { fmt } from '../../utils/number';
import { getResultCodeTitle } from './party-votes-stage';
import { sortForDisplay } from './utils';
import type { IChildBalance, ISheetCheck } from './vote-balance';
import { computeVoteBalance } from './vote-balance';
import { VoteBalancePanel } from './VoteBalance';

export interface IVoteSummary {
	party: string;
	vote: number;
}

const VoteSummary = () => {
	const {
		global: {
			state: { partyVotes, partyBallot, resultSummary, childCodes, resultType },
			updateState,
		},
		onStage,
	} = useGlobalContext();

	const getVoteSummary = (): IVoteSummary[] => {
		const totals = partyVotes
			.flatMap((i) =>
				i.votes.map((j) => ({
					partyId: j.partyId,
					label: j.label,
					vote: parseInt((j.votes || 0) + '') || 0,
				}))
			)
			.reduce((t, curr) => {
				if (t.has(curr.partyId)) {
					t.get(curr.partyId)!.vote += curr.vote;
				} else {
					t.set(curr.partyId, { ...curr });
				}
				return t;
			}, new Map<number, { partyId: number; vote: number; label: string }>());

		// Same order as the Party Vote Count grid the agent just typed into.
		return sortForDisplay(Array.from(totals.values()), partyBallot).map((i) => ({
			party: i.label,
			vote: i.vote,
		}));
	};

	const voteSummary = getVoteSummary();
	const balance = computeVoteBalance({ partyVotes, resultSummary, childCodes, resultType });
	const childLabel = (getResultCodeTitle(resultType as never).child || 'unit').toLowerCase() + 's';

	// Every jump lands on the field that needs correcting, not just the step.
	const jumpToSummaryField = (field: string) => {
		onStage('Result Summary');
		focusFieldById(field);
	};

	const openChild = (child: IChildBalance) => {
		// Select the real entry so `parent` and any future fields come along —
		// a reconstructed object would silently drop them.
		const target = childCodes.find((c) => c.codeId === child.id);
		if (!target) return;
		updateState('selectedChildCode', target);
		updateState('partyVoteStage', 'Party Votes');
		// The stage is currently unmounted; its mount effect would reset the above
		// to the child list, so flag the intent for it to pick up.
		updateState('partyVoteDeepLink', true);
		onStage('Party Vote Count');
	};

	return (
		<div className='f-column-33'>
			<VoteBalancePanel
				balance={balance}
				childLabel={childLabel}
				onCheckValidVotes={() => jumpToSummaryField('DataTotalValidVotes')}
				onReviewPartyVotes={() => onStage('Party Vote Count')}
				onFixCheck={(c: ISheetCheck) => jumpToSummaryField(c.fields[0])}
				onOpenChild={balance.children.length ? openChild : undefined}
			/>

			<div className='grid-wrapper-strict-25 gap-33'>
				{voteSummary.map((i, index) => (
					<div
						className='border-label rounded p-4 f-column-7'
						key={index}
					>
						<OverViewHeader title={i.party} />
						<p className='font-21'>{fmt(i.vote)}</p>
					</div>
				))}
			</div>

			{/* Ledger total — a rule above the sum, the way the sheet reads. */}
			<div className='vb-child vb-child--total'>
				<span className='vb-child__code' />
				<span className='vb-child__name'>Total party votes</span>
				<span className='vb-child__value'>{fmt(balance.counted)}</span>
			</div>
		</div>
	);
};

export default VoteSummary;
